import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { Bug, Inbox, Lightbulb, ListChecks, Info } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { UserLink } from "@/components/ui/user-link"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { MetricCard } from "@/components/domain/metric-card"
import { RowAvatar } from "@/components/domain/row-avatar"
import { FeedbackStackedBar } from "@/components/domain/feedback-stacked-bar"
import { FeedbackDetailDrawer } from "@/components/domain/feedback-detail-drawer"
import { fmtCompactRelative, fmtNumber } from "@/lib/format"
import { cn } from "@/lib/cn"
import type {
  FeedbackDrawerData,
  UserFeedbackProps,
} from "@/types/user_feedback"

// User Feedback v1 — read-only operator view. Backend hands us pre-aggregated
// totals + bar segments + tab buckets so the page is dumb. Action buttons,
// status workflow, sentiment, and occurrence count are intentionally absent
// in v1 because there's no data behind them yet (see plan
// /Users/zig/.claude/plans/update-al-lthe-internal-piped-iverson.md).
//
// Selection model: clicking a row sets local `selectedId` and we lazy-fetch
// /user_feedback/:id (Accept: application/json) for the drawer payload.

const TONE_BY_CATEGORY: Record<string, BadgeTone> = {
  bug:            "red",
  recommendation: "gold",
  unresolved:     "warning",
  general:        "neutral",
  rating:         "success",
  bad_behavior:   "error",
}

function toneFor(category: string): BadgeTone {
  return TONE_BY_CATEGORY[category] ?? "neutral"
}

const EXPLAINER = `Feedback items are surfaced from real agent conversations. \
Bugs are reproducible product defects users hit while chatting with an agent. \
Recommendations are feature ideas or enhancement requests. Unsupported queries \
("unresolved") are questions the agent didn't have a skill or knowledge for — \
useful as a backlog signal for what to teach next. Items here are read-only in \
v1; review and triage workflows are coming.`

export default function UserFeedbackIndex({
  rows,
  totals,
  bar,
  buckets,
  filter,
}: UserFeedbackProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerData, setDrawerData] = useState<FeedbackDrawerData | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [explainerOpen, setExplainerOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(filter.q ?? "")

  // Fetch detail when a row is selected. We deliberately don't prefetch — most
  // operators only open one or two items per visit.
  useEffect(() => {
    if (selectedId == null) {
      setDrawerData(null)
      return
    }
    let cancelled = false
    setDrawerLoading(true)
    fetch(`/user_feedback/${selectedId}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data: FeedbackDrawerData = await res.json()
        if (!cancelled) setDrawerData(data)
      })
      .catch(() => {
        if (!cancelled) setDrawerData(null)
      })
      .finally(() => {
        if (!cancelled) setDrawerLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId])

  function buildUrl(patch: Partial<{ category: string | null; q: string | null; from: string | null; to: string | null }>) {
    const merged = { ...filter, ...patch }
    const params = new URLSearchParams()
    if (merged.category) params.set("category", merged.category)
    if (merged.q) params.set("q", merged.q)
    if (merged.from) params.set("from", merged.from)
    if (merged.to) params.set("to", merged.to)
    const qs = params.toString()
    return qs ? `/user_feedback?${qs}` : "/user_feedback"
  }

  function setCategory(key: string | null) {
    router.visit(buildUrl({ category: key }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  function applySearch() {
    const next = searchValue.trim() || null
    router.visit(buildUrl({ q: next }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  function setDate(field: "from" | "to", value: string) {
    router.visit(buildUrl({ [field]: value || null }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const activeCategory = filter.category ?? "all"

  return (
    <AppShell>
      <PageHeader
        title="User Feedback"
        actions={
          <>
            <Button
              variant="ghost"
              title="Coming soon"
              onClick={(e) => e.preventDefault()}
              disabled
            >
              New category
            </Button>
            <Button
              variant="gold"
              title="Coming soon"
              onClick={(e) => e.preventDefault()}
              disabled
            >
              Export
            </Button>
          </>
        }
      />

      <div className="-mt-4 flex items-center gap-2 text-sm text-app-muted">
        <span>
          User-submitted bugs, feature ideas, and unresolved questions, surfaced
          from agent conversations.
        </span>
        <button
          type="button"
          onClick={() => setExplainerOpen(true)}
          aria-label="What is User Feedback?"
          className="inline-flex items-center justify-center rounded-full p-0.5 text-app-muted hover:text-app-text"
        >
          <Info size={14} strokeWidth={2} />
        </button>
      </div>

      <Dialog open={explainerOpen} onClose={() => setExplainerOpen(false)} size="md">
        <DialogHeader title="About feedback categories" />
        <DialogBody>
          <p className="text-sm leading-relaxed text-app-text">{EXPLAINER}</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setExplainerOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </Dialog>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard
          icon={<Inbox size={18} strokeWidth={2} />}
          label="Total feedback"
          value={fmtNumber(totals.all)}
        />
        <MetricCard
          icon={<ListChecks size={18} strokeWidth={2} />}
          label="All items"
          value={fmtNumber(totals.all)}
        />
        <MetricCard
          icon={<Bug size={18} strokeWidth={2} />}
          label="Bugs"
          value={fmtNumber(totals.bugs)}
        />
        <MetricCard
          icon={<Lightbulb size={18} strokeWidth={2} />}
          label="Recommendations"
          value={fmtNumber(totals.recommendations)}
        />
      </section>

      <Card className="space-y-3 p-5">
        <div className="text-sm font-medium text-app-text">Category mix</div>
        <FeedbackStackedBar segments={bar} />
      </Card>

      <section className="flex flex-wrap items-center gap-3">
        <Input
          value={searchValue}
          placeholder="Search title, description, customer…"
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applySearch()
          }}
          className="min-w-[260px] flex-1"
        />
        <Button
          variant="ghost"
          size="sm"
          disabled
          title="Coming soon"
        >
          Status: All
        </Button>
        <div className="flex items-center gap-2 text-xs text-app-muted">
          <input
            type="date"
            value={filter.from ?? ""}
            onChange={(e) => setDate("from", e.target.value)}
            className="rounded-control border border-border bg-white/5 px-2 py-1.5 text-app-text outline-none focus:border-brand-red/50"
          />
          <span>–</span>
          <input
            type="date"
            value={filter.to ?? ""}
            onChange={(e) => setDate("to", e.target.value)}
            className="rounded-control border border-border bg-white/5 px-2 py-1.5 text-app-text outline-none focus:border-brand-red/50"
          />
        </div>
        <Button variant="ghost" size="sm" disabled title="Coming soon">
          More filters
        </Button>
      </section>

      <section className="flex flex-wrap gap-2">
        {buckets.map((b) => {
          const active = activeCategory === b.key
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => setCategory(b.key === "all" ? null : String(b.key))}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-chip border px-3 py-1.5 text-xs font-medium transition",
                active
                  ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
                  : "border-border bg-white/5 text-app-text hover:bg-white/10",
              )}
            >
              {b.label}
              <span className={cn("text-[11px]", active ? "text-brand-red" : "text-app-muted")}>
                {b.count}
              </span>
            </button>
          )
        })}
      </section>

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_380px]">
        {rows.length === 0 ? (
          <Card variant="inset" className="p-6 text-sm text-app-muted">
            No feedback rows in this view yet.
          </Card>
        ) : (
          <Card className="overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-white/[0.03] text-xs uppercase tracking-wide text-app-subtle">
                  <th className="px-4 py-3 text-left">Title</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Source</th>
                  <th className="px-4 py-3 text-right">Last seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const selected = selectedId === r.id
                  const customerLabel =
                    r.customerName ?? r.customerEmail ?? "Unknown"
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      className={cn(
                        "cursor-pointer text-app-text transition",
                        selected
                          ? "bg-brand-redSoft/30"
                          : "hover:bg-white/5",
                      )}
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-app-text">{r.title}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge tone={toneFor(r.category)}>{r.categoryLabel}</Badge>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <RowAvatar
                            name={customerLabel}
                            className="h-8 w-8 text-sm"
                          />
                          <div onClick={(e) => e.stopPropagation()}>
                            <UserLink
                              id={r.appuserId}
                              name={r.customerName}
                              email={r.customerEmail}
                            />
                            {r.customerEmail && r.customerName && (
                              <div className="text-xs text-app-muted">
                                {r.customerEmail}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-app-text">{r.agentName ?? "—"}</div>
                        {r.skillName && (
                          <div className="text-xs text-app-muted">{r.skillName}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right align-top text-xs text-app-muted">
                        {fmtCompactRelative(r.createdAt)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Card>
        )}

        {selectedId == null ? (
          <FeedbackDetailDrawer data={null} onClose={() => setSelectedId(null)} />
        ) : drawerLoading && !drawerData ? (
          <Card className="p-6 text-sm text-app-muted">Loading…</Card>
        ) : (
          <FeedbackDetailDrawer
            data={drawerData}
            onClose={() => setSelectedId(null)}
          />
        )}
      </section>
    </AppShell>
  )
}
